// Edit an existing StackSet: 4-step wizard mirroring StackSetCreate but
// pre-filled from the deployment, PUTting only the CHANGED fields
// (UpdateStackSetRequest semantics: nil/empty = keep). Names are immutable on
// AWS StackSets, so there is no Name step.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, FormGroup,
  Grid, Paper, Radio, RadioGroup, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, insetPanelSx, sectionHeadingSx, statusChipSx, actionRowSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import LazyOrgTreeCheckbox from '../../../components/portal/LazyOrgTreeCheckbox';
import MultiSelectField from '../securityhub/MultiSelectField';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import { useLazyOrgTree } from '../../../hooks/useLazyOrgTree';
import { countAccountsUnderOUs } from '../../../utils/orgCount';
import {
  getStackSet, getStackSetsOrgChildren, getStackSetTemplate, listInstances, listOperations,
  updateStackSet, validateTemplate,
} from '../../../services/stacksetsApi';
import TemplateDiffView from './TemplateDiffView';
import AutoDeployOptions, { ROOT_TARGET_LABEL } from './AutoDeployOptions';

const STEPS = ['Template', 'Targets', 'Options', 'Review'];
const MAX_TEMPLATE_BYTES = 460800;
const ACTIVE = ['in_progress', 'updating', 'deleting'];

// One measure for every wizard step body so the form does not re-flow its
// width as the user advances (shared with StackSetCreate / StackSetsSetup).
const STEP_MAX_WIDTH = 720;

// Admin-console stepper: Metropolis labels, accent for the active step.
const stepperSx = {
  mb: 3,
  overflowX: 'auto',
  '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 500, color: CK.body },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
  '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
  '& .MuiStepIcon-root.Mui-active': { color: CK.blue },
  '& .MuiStepIcon-root.Mui-completed': { color: CK.green },
};

const parse = (raw, fallback) => { try { return JSON.parse(raw || ''); } catch { return fallback; } };

// Order-insensitive equality for string lists (regions, OU ids, account ids).
const sameSet = (a, b) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

// Canonical key for a {paramKey: value} map so maps compare order-insensitively.
const paramsKey = (m) => JSON.stringify(Object.entries(m).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));

export default function StackSetEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(null); // {dep, template, params, ous, accounts, regions, prefs, cells}
  const [busyReason, setBusyReason] = useState(null);

  const [templateBody, setTemplateBody] = useState('');
  const [refreshFromURL, setRefreshFromURL] = useState(true); // URL-sourced: pull latest by default
  const [validating, setValidating] = useState(false);
  const [summary, setSummary] = useState(null); // set only after re-validating a CHANGED template
  const [paramValues, setParamValues] = useState({});
  const [capsAccepted, setCapsAccepted] = useState(false);
  const [selectedOUs, setSelectedOUs] = useState([]); // [{id,name,...}]
  const [selectedAccounts, setSelectedAccounts] = useState([]); // [{id,name,type}]
  const [regions, setRegions] = useState([]);
  const [failureTolerance, setFailureTolerance] = useState(10);
  const [maxConcurrent, setMaxConcurrent] = useState(100);
  const [retainRemoved, setRetainRemoved] = useState('delete');
  const [submitting, setSubmitting] = useState(false);
  // Accounts under a ticked OU that are unticked ("unselected" exclusions).
  const [excludedAccounts, setExcludedAccounts] = useState([]); // [{id,name}]
  const [autoDeploy, setAutoDeploy] = useState('off');
  const [removalBehavior, setRemovalBehavior] = useState('delete');

  // Load the deployment, its current template, instances (for the blast-radius
  // preview) and operations (busy gate), then pre-fill every wizard field.
  // Extracted into a callback so the pre-load error branch's Retry button can
  // re-run it.
  const load = useCallback(async () => {
    setError(null);
    setBusyReason(null);
    try {
      const [d, t, i, o] = await Promise.all([
        getStackSet(id), getStackSetTemplate(id), listInstances(id), listOperations(id),
      ]);
      const dep = d?.data;
      if (!dep) throw new Error('StackSet not found');
      const ops = o?.data || [];
      if (dep.status === 'deleted') {
        setBusyReason('This StackSet has been deleted and can no longer be updated.');
      } else if (ACTIVE.includes(dep.status) || ops.some((op) => op.status === 'running')) {
        setBusyReason('This StackSet has an operation in progress — wait for it to finish before editing.');
      }
      const template = t?.data?.templateBody || '';
      const params = parse(dep.parameters, []);
      // Same `null` hazard as targetAccounts below, and now reachable: an
      // account-only StackSet stores no OUs, so targetOus is the nil slice.
      const parsedOUs = parse(dep.targetOus, []);
      const ous = Array.isArray(parsedOUs) ? parsedOUs : [];
      // Individual account targets. A StackSet created before accounts were
      // targetable has no targetAccounts at all, and a nil slice marshals to the
      // literal `null` — which JSON.parse ACCEPTS, so `parse`'s fallback never
      // fires for it. Both must read as "no accounts", never crash on .map.
      const parsedAccts = parse(dep.targetAccounts, []);
      const accts = Array.isArray(parsedAccts) ? parsedAccts : [];
      const regs = parse(dep.regions, []);
      const prefs = parse(dep.operationPrefs, {});
      // Exclusions: "unselected" (all regions) seed the picker; per-cell
      // "instance_deleted" ones only feed the blast-radius diff — they change
      // through the Detail page, never here.
      const parsedEx = parse(dep.excludedCells, []);
      const exclusions = Array.isArray(parsedEx) ? parsedEx : [];
      const unselected = exclusions.filter((x) => x.reason === 'unselected').map((x) => x.accountId);
      const deletedCells = exclusions
        .filter((x) => x.reason !== 'unselected')
        .map((x) => `${x.accountId}|${x.region}`);
      setLoaded({
        dep,
        template,
        params,
        ous,
        accounts: accts,
        regions: regs,
        prefs,
        unselected,
        deletedCells,
        // Live cells only — deleted/skipped rows are not stack instances.
        cells: (i?.data || [])
          .filter((r) => r.phase !== 'deleted' && r.phase !== 'skipped')
          .map((r) => `${r.accountId}|${r.region}`),
      });
      setTemplateBody(template);
      const pv = {};
      params.forEach((p) => { pv[p.key] = p.value; });
      setParamValues(pv);
      setSelectedOUs(ous.map((ou) => ({ id: ou.id, name: ou.name, type: 'OU' })));
      setSelectedAccounts(accts.map((a) => ({ id: a.id, name: a.name, type: 'ACCOUNT' })));
      setRegions(regs);
      setExcludedAccounts(unselected.map((a) => ({ id: a, name: '' })));
      setAutoDeploy(dep.autoDeploy ? 'on' : 'off');
      setRemovalBehavior(dep.retainOnAccountRemoval ? 'retain' : 'delete');
      if (prefs.failureTolerancePercentage != null) setFailureTolerance(prefs.failureTolerancePercentage);
      if (prefs.maxConcurrentPercentage != null) setMaxConcurrent(prefs.maxConcurrentPercentage);
    } catch (e) {
      setError(e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // One level per request: the whole-tree walk cost 2 Organizations calls per OU
  // serially and was cut off at 15s by the Envoy sidecar on a real customer org.
  // getStackSetsOrgChildren is a module-level export, so the reference is stable
  // and the hook's mount effect runs once.
  //
  // This block sits below `load` rather than with the other state on purpose:
  // runCount's dependency array reads `selectedOUs`/`selectedAccounts` at render
  // time, so it cannot be declared above those useStates without a TDZ error.
  // Do not tidy it upwards.
  const tree = useLazyOrgTree(getStackSetsOrgChildren);
  // {status, accounts, message}: 'idle' before a selection, 'counting' while the
  // selected subtrees are walked, 'done' with the exact ACTIVE accounts, 'error'
  // when a level failed — never a low number presented as fact. `message` carries
  // the failure reason, which is otherwise unreachable: it lands in
  // tree.errors[nodeId], which only renders on a row the user has not expanded.
  const [count, setCount] = useState({ status: 'idle', accounts: [], message: '' });

  // Counting walks ONLY the selected subtrees, sharing the picker's cache. A
  // generation counter discards a walk whose selection is already stale.
  const countGenRef = useRef(0);
  const runCount = useCallback(async () => {
    const gen = ++countGenRef.current;
    if (selectedOUs.length === 0 && selectedAccounts.length === 0) {
      setCount({ status: 'idle', accounts: [], message: '' });
      return;
    }
    setCount({ status: 'counting', accounts: [], message: '' });
    try {
      const res = await countAccountsUnderOUs(selectedOUs.map((o) => o.id), {
        getChildren: tree.loadChildren,
        cache: tree.cacheRef.current,
        isCancelled: () => countGenRef.current !== gen,
      });
      if (res.cancelled || countGenRef.current !== gen) return;
      // Explicit accounts cost no AWS calls — countAccountsUnderOUs with an empty
      // list issues no requests — so an account-only selection counts instantly.
      // The Set also collapses an account that is BOTH ticked outright and swept
      // up by a ticked ancestor OU, so it is never counted twice.
      const union = [...new Set([...res.accounts, ...selectedAccounts.map((a) => a.id)])];
      // ouAccounts kept apart: exclusions subtract from the OU expansion only.
      setCount({ status: 'done', accounts: union, ouAccounts: res.accounts, suspended: res.suspended || [], message: '' });
    } catch (e) {
      if (countGenRef.current !== gen) return;
      setCount({ status: 'error', accounts: [], message: e.message || '' });
    }
  }, [selectedOUs, selectedAccounts, tree.loadChildren, tree.cacheRef]);

  useEffect(() => {
    runCount();
    // Abandon an in-flight walk when the page goes away: nothing else will ever
    // advance the generation once this component is gone.
    return () => { countGenRef.current += 1; };
  }, [runCount]);

  // A re-read hierarchy invalidates any count computed from the old one, and any
  // walk still in flight against it. Without this, "Try again" would leave a
  // stale 'done' count holding the submit gate OPEN against a tree the user just
  // asked to discard — the count only re-runs when the selection changes.
  const refreshHierarchy = useCallback(() => {
    countGenRef.current += 1;
    setCount({ status: 'idle', accounts: [], message: '' });
    tree.refresh();
    if (selectedOUs.length > 0 || selectedAccounts.length > 0) runCount();
  }, [tree, selectedOUs, selectedAccounts, runCount]);

  // (OU expansion − excluded) ∪ explicit — the same set the backend deploys.
  const explicitIds = new Set(selectedAccounts.map((a) => a.id));
  const ouAccountSet = new Set(count.ouAccounts || []);
  const effectiveExcluded = count.status === 'done'
    ? excludedAccounts.filter((a) => ouAccountSet.has(a.id) && !explicitIds.has(a.id))
    : excludedAccounts.filter((a) => !explicitIds.has(a.id));
  const excludedIdSet = new Set(effectiveExcluded.map((a) => a.id));
  const previewAccounts = count.status === 'done'
    ? [...new Set([...(count.ouAccounts || []).filter((a) => !excludedIdSet.has(a)), ...explicitIds])]
    : count.accounts;
  const hasOUTargets = selectedOUs.length > 0;

  // Error and loading keep the page header mounted so only the card body under
  // it changes when the StackSet lands.
  if (error && !loaded) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title="Update StackSet" subtitle="CloudFormation StackSet" />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={load} />
        </motion.div>
      </motion.div>
    );
  }
  if (!loaded) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title="Update StackSet" subtitle="CloudFormation StackSet" />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading StackSet…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (busyReason) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title={`Update ${loaded.dep.name}`} subtitle="CloudFormation StackSet" />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>{busyReason}</Alert>
            <Button variant="outlined" onClick={() => navigate(`/stacksets/${id}`)}>Back to StackSet</Button>
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // --- changed-field detection (template compared as strings — no SHA client-side) ---
  // URL-sourced StackSets read their template from S3 on each deploy; "changed"
  // is the user's intent to pull the latest (the backend decides if the content
  // actually differs and no-ops otherwise).
  const isURLSourced = !!loaded.dep.templateSourceUrl;
  const templateChanged = isURLSourced ? refreshFromURL : (templateBody !== loaded.template);
  const storedParams = {};
  loaded.params.forEach((p) => { storedParams[p.key] = p.value; });
  const paramsChanged = paramsKey(paramValues) !== paramsKey(storedParams);
  const ousChanged = !sameSet(selectedOUs.map((o) => o.id), loaded.ous.map((o) => o.id));
  const accountsChanged = !sameSet(selectedAccounts.map((a) => a.id), (loaded.accounts || []).map((a) => a.id));
  const regionsChanged = !sameSet(regions, loaded.regions);
  const prefsChanged = Number(failureTolerance) !== Number(loaded.prefs.failureTolerancePercentage ?? 10)
    || Number(maxConcurrent) !== Number(loaded.prefs.maxConcurrentPercentage ?? 100);
  // Compared after the same filtering on both sides, so a stored exclusion
  // that simply stopped meaning anything (account left the OUs) is not "a change".
  const storedExcluded = (loaded.unselected || []).filter((a) => !explicitIds.has(a)
    && (count.status !== 'done' || ouAccountSet.has(a)));
  const excludedChanged = !sameSet(effectiveExcluded.map((a) => a.id), storedExcluded);
  const targetsChanged = ousChanged || accountsChanged || regionsChanged || excludedChanged;
  // Automatic deployment applies to OU targets only; without one there is
  // nothing to toggle, so the stored value is left as it is.
  const autoDeployChanged = hasOUTargets && (autoDeploy === 'on') !== !!loaded.dep.autoDeploy;
  const removalChanged = hasOUTargets && autoDeploy === 'on'
    && (removalBehavior === 'retain') !== !!loaded.dep.retainOnAccountRemoval;
  const autoChanged = autoDeployChanged || removalChanged;
  const nothingChanged = !templateChanged && !paramsChanged && !targetsChanged && !prefsChanged && !autoChanged;
  // A toggle-only save is metadata: no redeploy, so it skips the review diff.
  const autoOnlyChange = autoChanged && !templateChanged && !paramsChanged && !targetsChanged && !prefsChanged;

  // Blast-radius preview vs the CURRENT instances (backend re-diffs authoritatively).
  //
  // When the targets changed but the walk has not finished, addCount/removeCount
  // stay at their 0 initialisers — which would render as "plus 0 new and −0
  // removed", a confident claim about a number we do not have. So Review shows a
  // placeholder in place of the 0 and fills the figures in when the walk lands.
  // Submission does NOT wait for it: walking a large organization took ~40s,
  // and the backend re-diffs the targets itself on submit. While the figure is
  // unknown, the delete/retain choice for removed stacks is shown regardless,
  // so the user still decides what happens to any instance the update removes.
  const blastRadiusUnknown = targetsChanged && count.status !== 'done';
  const pendingFigure = count.status === 'counting' ? 'counting…' : 'unknown';
  const instanceCount = loaded.cells.length;
  let addCount = 0;
  let removeCount = 0;
  // Gated on a COMPLETED count: a diff computed mid-walk would understate the
  // blast radius shown before the user confirms the update.
  if (targetsChanged && count.status === 'done') {
    const desired = new Set();
    // Deleted instances stay excluded per cell, exactly as the backend diffs.
    const deletedCells = new Set(loaded.deletedCells || []);
    previewAccounts.forEach((a) => regions.forEach((r) => {
      const cell = `${a}|${r}`;
      if (!deletedCells.has(cell)) desired.add(cell);
    }));
    const current = new Set(loaded.cells);
    // A suspended account's stacks are never removed by an update (AWS skips
    // it, and the backend drops those cells), so they are not counted here.
    const suspendedIds = new Set(count.suspended || []);
    desired.forEach((c) => { if (!current.has(c)) addCount += 1; });
    current.forEach((c) => {
      if (!desired.has(c) && !suspendedIds.has(c.split('|')[0])) removeCount += 1;
    });
  }

  const capsRequired = templateChanged && (summary?.capabilities || []).length > 0;

  // Every template-body change funnels through here. Validation overwrites
  // paramValues with the NEW template's keys, so if the body reverts to the
  // deployed template the params must snap back to the stored values —
  // otherwise stale new-template keys would be submitted as "changed" params
  // against the old template.
  const changeTemplate = (body) => {
    setTemplateBody(body);
    setSummary(null);
    if (body === loaded.template) setParamValues({ ...storedParams });
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_TEMPLATE_BYTES) {
      setError(`Template exceeds the ${Math.floor(MAX_TEMPLATE_BYTES / 1024)}KB limit`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { changeTemplate(String(reader.result)); setError(null); };
    reader.onerror = () => setError('Could not read the selected file');
    reader.readAsText(file);
    // Reset the input so re-selecting the same file fires onChange again
    // (readAsText has already captured the file; clearing is safe here).
    e.target.value = '';
  };

  // Re-validate only when the template body changed; merge the new template's
  // parameters with the stored values (stored value wins, else the default).
  const runValidate = async () => {
    setValidating(true);
    setError(null);
    try {
      const res = await validateTemplate(
        isURLSourced ? '' : templateBody,
        isURLSourced ? loaded.dep.templateSourceUrl : '',
      );
      const s = res?.data || {};
      setSummary(s);
      const next = {};
      (s.parameters || []).forEach((p) => {
        next[p.key] = storedParams[p.key] !== undefined ? storedParams[p.key] : (p.defaultValue || '');
      });
      setParamValues(next);
      setCapsAccepted(!(s.capabilities || []).length);
      setActiveStep(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setValidating(false);
    }
  };

  // Only send changed fields — nil means "keep" on the backend.
  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {};
      if (isURLSourced) {
        if (refreshFromURL) {
          body.refreshFromSourceUrl = true; // backend re-reads the URL & redeploys if changed
          if (summary) body.capabilities = summary.capabilities || [];
        }
      } else if (templateChanged) {
        body.templateBody = templateBody;
        // The re-validation summary carries the NEW template's capability set.
        body.capabilities = summary?.capabilities || [];
      }
      if (paramsChanged || templateChanged) {
        // noEcho travels with each parameter so the Detail page can mask it:
        // from the re-validation summary when the template changed, else from
        // the stored parameters. A template change always re-sends parameters
        // — the new summary's noEcho flags are authoritative even when keys
        // and values are identical.
        const noEchoFor = (key) => (summary
          ? !!(summary.parameters || []).find((p) => p.key === key)?.noEcho
          : !!loaded.params.find((p) => p.key === key)?.noEcho);
        body.parameters = Object.entries(paramValues).map(([key, value]) => ({ key, value, noEcho: noEchoFor(key) }));
      }
      if (ousChanged) body.targetOus = selectedOUs.map((o) => ({ id: o.id, name: o.name }));
      if (accountsChanged) body.targetAccounts = selectedAccounts.map((a) => ({ id: a.id, name: a.name }));
      if (regionsChanged) body.regions = regions;
      // Sent only when it changed: nil keeps the stored exclusions.
      if (excludedChanged) body.excludedAccounts = effectiveExcluded.map((a) => a.id);
      if (autoDeployChanged) body.autoDeploy = autoDeploy === 'on';
      if (removalChanged) body.retainOnAccountRemoval = removalBehavior === 'retain';
      if (prefsChanged) {
        body.operationPrefs = {
          failureTolerancePercentage: Number(failureTolerance),
          maxConcurrentPercentage: Number(maxConcurrent),
          regionConcurrencyType: 'PARALLEL',
        };
      }
      body.retainStacks = retainRemoved === 'retain'; // applies to instances removed by target shrink
      await updateStackSet(id, body);
      navigate(`/stacksets/${id}`);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  // Parameters form: the new template's summary when re-validated, else the
  // stored key/value pairs (no re-validation needed for an unchanged template).
  const paramFields = summary
    ? (summary.parameters || [])
    : loaded.params.map((p) => ({ key: p.key, description: '', noEcho: !!p.noEcho }));

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<LayersIcon sx={{ fontSize: 24 }} />}
          title={`Update ${loaded.dep.name}`}
          subtitle="StackSet names are immutable — everything else can change"
        />
      </motion.div>
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>
        </motion.div>
      )}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <Stepper activeStep={activeStep} sx={stepperSx}>
            {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>

          {activeStep === 0 && isURLSourced && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              <TextField
                label="Template S3 URL" value={loaded.dep.templateSourceUrl}
                InputProps={{ readOnly: true }}
                helperText="This StackSet reads its template from S3 on every deploy. Update the file at this URL, then pull the latest below to roll it out."
              />
              <FormControlLabel
                control={<Checkbox checked={refreshFromURL} onChange={(e) => { setRefreshFromURL(e.target.checked); setSummary(null); }} />}
                label="Pull the latest template from this S3 URL and redeploy if it changed"
              />
              <Box sx={actionRowSx}>
                <Button onClick={() => navigate(`/stacksets/${id}`)}>Cancel</Button>
                {refreshFromURL ? (
                  <Button variant="contained" disabled={validating} onClick={runValidate}>
                    {validating ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Validate latest & continue'}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => setActiveStep(1)}>Next</Button>
                )}
              </Box>
            </Box>
          )}

          {activeStep === 0 && !isURLSourced && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              {!templateChanged && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  Template unchanged — parameters keep their stored values and no
                  capability re-acknowledgement is needed.
                </Alert>
              )}
              <Button variant="outlined" component="label" sx={{ alignSelf: 'flex-start' }}>
                Upload template file (.json / .yaml)
                <input hidden type="file" accept=".json,.yaml,.yml,.template" onChange={onFile} />
              </Button>
              <TextField
                label="Template body" value={templateBody}
                onChange={(e) => changeTemplate(e.target.value)}
                multiline rows={14} sx={{ fontFamily: 'monospace' }}
              />
              <Box sx={actionRowSx}>
                <Button onClick={() => navigate(`/stacksets/${id}`)}>Cancel</Button>
                {templateChanged ? (
                  <Button variant="contained" disabled={!templateBody.trim() || validating} onClick={runValidate}>
                    {validating ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Validate & continue'}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => setActiveStep(1)}>Next</Button>
                )}
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              {paramFields.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Template parameters</Typography>
                  <Grid container spacing={2}>
                    {paramFields.map((p) => (
                      <Grid key={p.key} size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label={p.key} helperText={p.description} type={p.noEcho ? 'password' : 'text'}
                          value={paramValues[p.key] ?? ''} onChange={(e) => setParamValues({ ...paramValues, [p.key]: e.target.value })}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Targets</Typography>
              {tree.topError ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} action={
                  <Button color="inherit" size="small" onClick={refreshHierarchy}>Try again</Button>
                }>
                  Could not load the organization hierarchy: {tree.topError}
                </Alert>
              ) : !tree.root ? (
                <PortalLoading label="Loading the organization hierarchy…" py={4} />
              ) : (
                <Paper elevation={0} sx={{ ...insetPanelSx, p: 1, maxHeight: 320, overflow: 'auto', mb: 2 }}>
                  <LazyOrgTreeCheckbox
                    node={tree.root}
                    tree={tree}
                    rootLabel={ROOT_TARGET_LABEL}
                    excludedIds={excludedAccounts.map((a) => a.id)}
                    onToggleExclude={(sel) => setExcludedAccounts((cur) => (cur.some((c) => c.id === sel.id)
                      ? cur.filter((c) => c.id !== sel.id) : [...cur, { id: sel.id, name: sel.name }]))}
                    // Both lists, or an account seeded from the deployment would
                    // render UNticked while still being a live target.
                    selectedIds={[...selectedOUs, ...selectedAccounts]}
                    onToggleSelect={(sel) => {
                      // OUs and individual accounts are separate fields on the
                      // update request, so they are kept in separate lists — the
                      // backend expects OU ids only in targetOus.
                      const toggle = (cur) => (cur.some((c) => c.id === sel.id)
                        ? cur.filter((c) => c.id !== sel.id) : [...cur, sel]);
                      // Match kinds explicitly rather than defaulting the
                      // leftovers into OUs: an unrecognised kind the API grows
                      // later would otherwise be sent as an OU id in targetOus.
                      if (sel.type === 'ACCOUNT') setSelectedAccounts(toggle);
                      else if (sel.type === 'OU' || sel.type === 'ORGANIZATIONAL_UNIT' || sel.type === 'ROOT') setSelectedOUs(toggle);
                    }}
                  />
                </Paper>
              )}
              {/* A walk in flight is pending, not failed, and an empty selection
                  is neither — only a genuine failure reads as "unavailable". */}
              <Alert severity={count.status === 'error' ? 'warning' : 'info'} sx={{ mb: 2, borderRadius: '8px' }}>
                {count.status === 'idle' ? 'Select one or more OUs or individual accounts to see how many accounts will receive stacks.'
                  : count.status === 'counting' ? 'Counting accounts…'
                    : count.status === 'done' ? (
                      <>
                        {previewAccounts.length} account{previewAccounts.length === 1 ? '' : 's'} will receive stacks
                        {regions.length > 0 ? ` in ${regions.length} region${regions.length === 1 ? '' : 's'} (${previewAccounts.length * regions.length} stack instances)` : ''}.
                        {effectiveExcluded.length > 0 && ` ${effectiveExcluded.length} account${effectiveExcluded.length === 1 ? '' : 's'} excluded.`}
                      </>
                    ) : (
                      <>
                        Account count unavailable{count.message ? `: ${count.message}` : ''} — a level of the
                        hierarchy failed to load. The update can still be submitted; the exact changes are
                        computed when it runs.
                        {' '}
                        <Button size="small" onClick={runCount} sx={{ textTransform: 'none' }}>Retry</Button>
                      </>
                    )}
              </Alert>
              <MultiSelectField
                label="Regions" options={AWS_REGIONS} value={regions} onChange={setRegions}
              />
              <Box sx={{ ...actionRowSx, mt: 2 }}>
                <Button onClick={() => setActiveStep(0)}>Back</Button>
                <Button
                  variant="contained"
                  disabled={(selectedOUs.length === 0 && selectedAccounts.length === 0) || regions.length === 0}
                  onClick={() => setActiveStep(2)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              <Typography sx={{ ...sectionHeadingSx }}>Deployment options</Typography>
              <AutoDeployOptions
                autoDeploy={autoDeploy}
                onAutoDeployChange={setAutoDeploy}
                removal={removalBehavior}
                onRemovalChange={setRemovalBehavior}
                hasOUTargets={hasOUTargets}
              />
              <TextField
                label="Failure tolerance (%)" type="number" value={failureTolerance}
                onChange={(e) => setFailureTolerance(e.target.value)}
                helperText="Percentage of accounts per region that can fail before the operation stops"
              />
              <TextField
                label="Max concurrent accounts (%)" type="number" value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
              />
              {capsRequired && (
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={capsAccepted} onChange={(e) => setCapsAccepted(e.target.checked)} />}
                    label={`I acknowledge that this template requires: ${(summary.capabilities || []).join(', ')}`}
                  />
                </FormGroup>
              )}
              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(1)}>Back</Button>
                {autoOnlyChange ? (
                  <Button variant="contained" disabled={submitting} onClick={submit}>
                    {submitting ? 'Saving…' : 'Save deployment options'}
                  </Button>
                ) : (
                  <Button variant="contained" disabled={capsRequired && !capsAccepted} onClick={() => setActiveStep(3)}>Next</Button>
                )}
              </Box>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
              <Box sx={{ ...insetPanelSx, display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip size="small" label={`Name: ${loaded.dep.name}`} sx={statusChipSx('neutral')} />
                <Chip size="small" label={`Template: ${templateChanged ? `changes to v${loaded.dep.templateVersion + 1}` : `v${loaded.dep.templateVersion} (unchanged)`}`} sx={statusChipSx(templateChanged ? 'warning' : 'neutral')} />
                <Chip size="small" label={`Parameters: ${paramsChanged ? 'changed' : 'unchanged'}`} sx={statusChipSx(paramsChanged ? 'warning' : 'neutral')} />
                <Chip size="small" label={`Targets: ${targetsChanged ? 'changed' : 'unchanged'}`} sx={statusChipSx(targetsChanged ? 'warning' : 'neutral')} />
                <Chip size="small" label={`Preferences: ${prefsChanged ? 'changed' : 'unchanged'}`} sx={statusChipSx(prefsChanged ? 'warning' : 'neutral')} />
                {effectiveExcluded.length > 0 && (
                  <Chip size="small" label={`Excluded: ${effectiveExcluded.length} account${effectiveExcluded.length === 1 ? '' : 's'}`} sx={statusChipSx(excludedChanged ? 'warning' : 'neutral')} />
                )}
                {targetsChanged && (count.status === 'done' ? (
                  <Chip size="small" label={`Will remove: ${removeCount} instance${removeCount === 1 ? '' : 's'}`} sx={statusChipSx(removeCount > 0 ? 'error' : 'neutral')} />
                ) : (
                  <Chip size="small" label={`Will remove: ${pendingFigure}`} sx={statusChipSx(count.status === 'counting' ? 'info' : 'warning')} />
                ))}
                <Chip
                  size="small"
                  label={`Automatic deployment: ${hasOUTargets && autoDeploy === 'on' ? 'On' : 'Off'}${autoChanged ? ' (changed)' : ''}`}
                  sx={statusChipSx(autoChanged ? 'warning' : 'neutral')}
                />
              </Box>
              {/* Template change preview — the StackSet stand-in for a change set.
                  Body-sourced: diff the deployed template against the edited one.
                  URL-sourced: no client-side "before" (the URL is read live), so
                  point at the per-instance CloudFormation events instead. */}
              {templateChanged && !isURLSourced && (
                <TemplateDiffView oldText={loaded.template} newText={templateBody} />
              )}
              {templateChanged && isURLSourced && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                  Pulling the latest template from S3. CloudFormation computes the exact
                  per-account resource changes on deploy — watch each instance&apos;s live
                  events on the StackSet page.
                </Alert>
              )}
              {nothingChanged ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                  Nothing has changed — there is no update to submit.
                </Alert>
              ) : blastRadiusUnknown ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                  Updating a StackSet redeploys to EVERY existing stack instance
                  ({instanceCount} instances) — plus {pendingFigure} new and −{pendingFigure} removed
                  from target changes.
                  {' '}
                  {count.status === 'counting'
                    ? 'The accounts under the selected targets are still being counted; the figures fill in here when the count finishes.'
                    : `The target change could not be sized${count.message ? ` (${count.message})` : ''} — a level of the organization hierarchy failed to load.`}
                  {' '}
                  You can submit now: the exact instances to create and remove are computed when the update runs,
                  and removed instances follow the choice below.
                  {count.status === 'error' && (
                    <>
                      {' '}
                      <Button size="small" onClick={runCount} sx={{ textTransform: 'none' }}>Retry count</Button>
                    </>
                  )}
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                  Updating a StackSet redeploys to EVERY existing stack instance
                  ({instanceCount} instances) — plus {addCount} new and −{removeCount} removed
                  from target changes.
                </Alert>
              )}
              {/* Shown while the count is unknown too: the update may remove
                  instances, so the delete/retain choice must be on screen. */}
              {targetsChanged && (removeCount > 0 || blastRadiusUnknown) && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
                    {blastRadiusUnknown
                      ? `Removed targets (${pendingFigure})`
                      : `Removed targets (${removeCount} instance${removeCount === 1 ? '' : 's'})`}
                  </Typography>
                  <RadioGroup value={retainRemoved} onChange={(e) => setRetainRemoved(e.target.value)}>
                    <FormControlLabel value="delete" control={<Radio />} label="Delete the removed accounts' stacks — destroys their resources" />
                    <FormControlLabel value="retain" control={<Radio />} label="Retain the removed accounts' stacks — resources stay (and keep billing)" />
                  </RadioGroup>
                </Box>
              )}
              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(2)}>Back</Button>
                <Button variant="contained" disabled={submitting || nothingChanged} onClick={submit}>
                  {submitting ? 'Updating…' : 'Update StackSet'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
}
